import type { TranslationKey } from "@/src/i18n";
import type { WallObjectCategory, WallObjectType } from "@/src/types/map";

/**
 * How the renderer draws a wall object. Kept declarative so a new type is a
 * catalogue entry plus a draw branch, not a new code path through placement.
 */
export interface WallObjectStyle {
  /** Door leaf: one swing arc, two mirrored arcs, or no leaf at all. */
  leaf: "single" | "double" | "none";
  /** Centre glyph, following standard dungeon-key convention (e.g. "S" for secret). */
  glyph?: string;
  /** Vertical bar count for portcullis/grating and fixed bars. */
  bars?: number;
  /** Direction arrow, for one-way doors. */
  arrow?: boolean;
  /** Wavy line, for curtains. */
  wavy?: boolean;
  /** Gap treatment for openings that have no leaf. */
  opening?: "full" | "window" | "slit";
  /** Small keyhole/padlock mark. */
  lock?: boolean;
  /** Cross-bar across the doorway, for stuck/barred. */
  barred?: boolean;
}

export interface WallObjectDefinition {
  id: WallObjectType;
  category: WallObjectCategory;
  /** Every member of this family snaps and rotates to the nearest wall. */
  placement: "wall-snap";
  defaultWidthCells: number;
  defaultHeightCells: number;
  style: WallObjectStyle;
  labelKey: TranslationKey;
  /** DM-only features are drawn with distinct styling and hidden in player view (Phase 5). */
  dmOnly: boolean;
}

export const WALL_OBJECT_CATALOG: WallObjectDefinition[] = [
  // --- doors ---------------------------------------------------------------
  { id: "single", category: "doors", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "single" }, labelKey: "wall.single", dmOnly: false },
  { id: "double", category: "doors", placement: "wall-snap", defaultWidthCells: 1.6, defaultHeightCells: 0.35,
    style: { leaf: "double" }, labelKey: "wall.double", dmOnly: false },
  { id: "locked", category: "doors", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "single", lock: true }, labelKey: "wall.locked", dmOnly: false },
  { id: "stuck", category: "doors", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "single", barred: true }, labelKey: "wall.stuck", dmOnly: false },
  { id: "one_way", category: "doors", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "single", arrow: true }, labelKey: "wall.one_way", dmOnly: false },

  // --- hidden (DM-only by default) ----------------------------------------
  { id: "secret", category: "hidden", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", glyph: "S", opening: "full" }, labelKey: "wall.secret", dmOnly: true },
  { id: "concealed", category: "hidden", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", glyph: "C", opening: "full" }, labelKey: "wall.concealed", dmOnly: true },

  // --- barriers ------------------------------------------------------------
  { id: "portcullis", category: "barriers", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", bars: 5 }, labelKey: "wall.portcullis", dmOnly: false },
  { id: "bars", category: "barriers", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", bars: 4 }, labelKey: "wall.bars", dmOnly: false },
  { id: "curtain", category: "barriers", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", wavy: true }, labelKey: "wall.curtain", dmOnly: false },

  // --- openings ------------------------------------------------------------
  { id: "archway", category: "openings", placement: "wall-snap", defaultWidthCells: 1, defaultHeightCells: 0.35,
    style: { leaf: "none", opening: "full" }, labelKey: "wall.archway", dmOnly: false },
  { id: "window", category: "openings", placement: "wall-snap", defaultWidthCells: 0.8, defaultHeightCells: 0.35,
    style: { leaf: "none", opening: "window" }, labelKey: "wall.window", dmOnly: false },
  { id: "arrow_slit", category: "openings", placement: "wall-snap", defaultWidthCells: 0.6, defaultHeightCells: 0.35,
    style: { leaf: "none", opening: "slit" }, labelKey: "wall.arrow_slit", dmOnly: false },
];

export const DEFAULT_WALL_TYPE: WallObjectType = "single";

const BY_ID = new Map(WALL_OBJECT_CATALOG.map((entry) => [entry.id, entry]));

/** Falls back to the plain single door, which is always present in the catalogue. */
export const getWallObject = (type: WallObjectType | undefined): WallObjectDefinition =>
  (type ? BY_ID.get(type) : undefined) ?? WALL_OBJECT_CATALOG[0]!;

export const isWallObjectType = (value: unknown): value is WallObjectType =>
  typeof value === "string" && BY_ID.has(value as WallObjectType);

export const WALL_OBJECT_CATEGORIES: readonly WallObjectCategory[] =
  ["doors", "hidden", "barriers", "openings"];

/** Cycles through the catalogue in order; used by the keyboard shortcut. */
export const nextWallType = (current: WallObjectType, step = 1): WallObjectType => {
  const index = WALL_OBJECT_CATALOG.findIndex((entry) => entry.id === current);
  const safeIndex = index < 0 ? 0 : index;
  const count = WALL_OBJECT_CATALOG.length;
  return WALL_OBJECT_CATALOG[(safeIndex + step + count) % count]!.id;
};
